import { z } from 'zod';

/**
 * Email block AST — the canonical representation of an email template.
 *
 * Canvas state is persisted as a tree of these nodes in `email_template_version.ast_json`.
 * Each block type has its own `props` schema. All 11 block types from PRD §5.3 are
 * represented: hero, heading, text, button, image, columns (2/3), divider, spacer,
 * product, footer, social.
 */

const blockIdSchema = z.string().min(1).max(64);

const commonProps = z.object({
  paddingTop: z.number().int().min(0).max(128).optional(),
  paddingBottom: z.number().int().min(0).max(128).optional(),
  paddingX: z.number().int().min(0).max(128).optional(),
  backgroundColor: z.string().optional(),
  /**
   * If true, the renderer emits a `@media (prefers-color-scheme: dark)` rule
   * that swaps background/text to `darkBackgroundColor` / `darkColor` on
   * supporting clients (Apple Mail, modern Gmail web/iOS, Outlook web).
   * Falls back to the light-mode values everywhere else — no effect on
   * clients that don't honor the media query.
   */
  darkMode: z.boolean().optional(),
  darkBackgroundColor: z.string().optional(),
  darkColor: z.string().optional(),
});

export const heroBlockSchema = z.object({
  id: blockIdSchema,
  type: z.literal('hero'),
  props: commonProps.extend({
    title: z.string(),
    subtitle: z.string().optional(),
    imageUrl: z.string().url().optional(),
    alignment: z.enum(['left', 'center']).default('center'),
  }),
});

export const headingBlockSchema = z.object({
  id: blockIdSchema,
  type: z.literal('heading'),
  props: commonProps.extend({
    text: z.string(),
    level: z.enum(['h1', 'h2', 'h3']).default('h2'),
    alignment: z.enum(['left', 'center', 'right']).default('left'),
  }),
});

export const textBlockSchema = z.object({
  id: blockIdSchema,
  type: z.literal('text'),
  props: commonProps.extend({
    text: z.string(),
    alignment: z.enum(['left', 'center', 'right']).default('left'),
    size: z.enum(['xs', 'sm', 'md', 'lg']).default('md'),
  }),
});

export const buttonBlockSchema = z.object({
  id: blockIdSchema,
  type: z.literal('button'),
  props: commonProps.extend({
    label: z.string(),
    href: z.string(),
    variant: z.enum(['primary', 'secondary', 'ghost']).default('primary'),
    alignment: z.enum(['left', 'center', 'right']).default('center'),
    fullWidth: z.boolean().default(false),
  }),
});

export const imageBlockSchema = z.object({
  id: blockIdSchema,
  type: z.literal('image'),
  props: commonProps.extend({
    src: z.string().url(),
    alt: z.string().default(''),
    href: z.string().url().optional(),
    width: z.number().int().positive().optional(),
    alignment: z.enum(['left', 'center', 'right']).default('center'),
  }),
});

export const dividerBlockSchema = z.object({
  id: blockIdSchema,
  type: z.literal('divider'),
  props: commonProps.extend({
    thickness: z.number().int().min(1).max(4).default(1),
    color: z.string().optional(),
  }),
});

export const spacerBlockSchema = z.object({
  id: blockIdSchema,
  type: z.literal('spacer'),
  props: z.object({
    height: z.number().int().min(4).max(128).default(24),
  }),
});

// Columns block — children are stored as raw JSON and re-validated by the
// recursive blockSchema below. We can't put a lazy ref inside a
// discriminatedUnion in zod, so columns' children use z.any() here and the full
// AST document is re-walked / validated at render time.
export const columnsBlockSchema = z.object({
  id: blockIdSchema,
  type: z.literal('columns'),
  props: z.object({
    ratio: z.enum(['1:1', '1:2', '2:1']).default('1:1'),
    gap: z.number().int().min(0).max(48).default(16),
  }),
  children: z.array(z.array(z.any())).min(2).max(3),
});

export const productBlockSchema = z.object({
  id: blockIdSchema,
  type: z.literal('product'),
  props: commonProps.extend({
    name: z.string(),
    price: z.string(),
    imageUrl: z.string().url(),
    href: z.string().url(),
    description: z.string().optional(),
  }),
});

export const footerBlockSchema = z.object({
  id: blockIdSchema,
  type: z.literal('footer'),
  props: commonProps.extend({
    companyName: z.string(),
    address: z.string().optional(),
    unsubscribeUrl: z.string().url(),
    preferencesUrl: z.string().url().optional(),
  }),
});

export const socialBlockSchema = z.object({
  id: blockIdSchema,
  type: z.literal('social'),
  props: commonProps.extend({
    icons: z
      .array(
        z.object({
          network: z.enum(['twitter', 'instagram', 'linkedin', 'facebook', 'github', 'youtube']),
          url: z.string().url(),
        }),
      )
      .max(10),
    alignment: z.enum(['left', 'center', 'right']).default('center'),
  }),
});

export const blockSchema = z.discriminatedUnion('type', [
  heroBlockSchema,
  headingBlockSchema,
  textBlockSchema,
  buttonBlockSchema,
  imageBlockSchema,
  dividerBlockSchema,
  spacerBlockSchema,
  columnsBlockSchema,
  productBlockSchema,
  footerBlockSchema,
  socialBlockSchema,
]);

export type HeroBlock = z.infer<typeof heroBlockSchema>;
export type HeadingBlock = z.infer<typeof headingBlockSchema>;
export type TextBlock = z.infer<typeof textBlockSchema>;
export type ButtonBlock = z.infer<typeof buttonBlockSchema>;
export type ImageBlock = z.infer<typeof imageBlockSchema>;
export type DividerBlock = z.infer<typeof dividerBlockSchema>;
export type SpacerBlock = z.infer<typeof spacerBlockSchema>;
export type ColumnsBlock = {
  id: string;
  type: 'columns';
  props: { ratio: '1:1' | '1:2' | '2:1'; gap: number };
  children: Block[][];
};
export type ProductBlock = z.infer<typeof productBlockSchema>;
export type FooterBlock = z.infer<typeof footerBlockSchema>;
export type SocialBlock = z.infer<typeof socialBlockSchema>;

export type Block =
  | HeroBlock
  | HeadingBlock
  | TextBlock
  | ButtonBlock
  | ImageBlock
  | DividerBlock
  | SpacerBlock
  | ColumnsBlock
  | ProductBlock
  | FooterBlock
  | SocialBlock;

export type BlockType = Block['type'];

export const ALL_BLOCK_TYPES: readonly BlockType[] = [
  'hero',
  'heading',
  'text',
  'button',
  'image',
  'columns',
  'divider',
  'spacer',
  'product',
  'footer',
  'social',
] as const;

export const astDocumentSchema = z.object({
  version: z.literal(1),
  blocks: z.array(blockSchema),
  tokens: z
    .object({
      bgColor: z.string().optional(),
      fontFamily: z.string().optional(),
      accentColor: z.string().optional(),
    })
    .optional(),
});

export type AstDocument = z.infer<typeof astDocumentSchema>;
